import { createRoute, useParams } from '@tanstack/react-router';
import { msg, T } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { orpc } from '@/api/rpc/client';
import { Spinner } from '@/components/ui/spinner';
import { useAgentPage } from 'src/api/rpc/hooks/agent-pages/useAgentPages';
// fallow-ignore-next-line circular-dependency
import { Route as pathlessAuthenticatedRoute } from '../../route';
import { requirePermission } from '../../route';
import { AgentPageWithDefinition } from '../components/AgentPageWithDefinition';
import { agentPageSearchSchema } from '../agentRunHistoryFilter';

const AGENT_PAGE_CRUMB_FALLBACK = msg('Agent page', {
  context: 'breadcrumb fallback when an agent page name cannot be loaded',
});

function AgentPageRoutePage() {
  const { agentPageId } = useParams({
    from: '/authenticated/agent-pages/$agentPageId',
  });
  const { data: page, isLoading, error } = useAgentPage(agentPageId);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (error || !page) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <T context="empty state when an agent page does not exist or is not visible to the user">
          This page does not exist or you do not have access to it.
        </T>
      </div>
    );
  }

  // Key by page id so navigating between agent pages remounts the body
  // (resetting the selected run and the history window).
  return <AgentPageWithDefinition key={page.id} page={page} />;
}

/**
 * Generic org-configured agent page (`ai_agent_pages`) — one background agent
 * mounted in the sidebar by the pages sidebar merge. Per-page visibility (the
 * user-group audience) is enforced server-side: `agentPages.get` returns
 * NOT_FOUND for non-visible pages. Read on BackgroundAgent is the only gate;
 * the agent monitor flag does not hide a page a team was given.
 */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agent-pages/$agentPageId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.BackgroundAgent },
        ],
      },
    },
  },
  component: AgentPageRoutePage,
  validateSearch: agentPageSearchSchema,
  loader: async ({ params, context }) => {
    // Fetch the page name for the breadcrumb, cached via queryClient.
    try {
      const page = await context.queryClient.fetchQuery(
        orpc.agentPages.get.queryOptions({
          input: { id: params.agentPageId },
        }),
      );
      return { crumb: page.name };
    } catch {
      // Left encoded: the breadcrumb bar decodes it in the active locale.
      return { crumb: AGENT_PAGE_CRUMB_FALLBACK };
    }
  },
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.BackgroundAgent,
    });
  },
});
